import { useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Box, IconButton, IconButtonProps, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import EntityMediaOverview from "../entity-media-overview/EntityMediaOverview";
import { useConfig } from "../../contexts/ConfigContext";
import { usePopupActions } from "../../store/popupStore";
import { useSelectionActions } from "../../store/selection";
import useDebounceCallback from "../../hooks/useDebounceCallback";
import { useMediaActions } from "../../store/media";

import type { MediaItemDetailed } from "../../types";

type EntityMediaCarouselProps = {
  items: MediaItemDetailed[];
  currentItem: MediaItemDetailed;
  index: number;
  onChange?: (cursor: number, item?: MediaItemDetailed) => void;
};

function SliderButton({ sx, ...rest }: IconButtonProps) {
  return (
    <IconButton
      {...rest}
      sx={{
        height: "2.5rem",
        width: "2.5rem",
        backgroundColor: "#FFFFFF33",
        color: "grey.400",
        backdropFilter: "blur(2px)",
        border: "1px solid",
        transition: "300ms linear",
        ":hover": {
          backgroundColor: "primary.main",
          borderColor: "primary.main",
          color: "secondary.main"
        },
        ":disabled": {
          color: "grey.500"
        },
        ...sx
      }}
    />
  );
}

export default function EntityMediaCarousel({ items = [], index, onChange, currentItem }: EntityMediaCarouselProps) {
  const [cursor, setCursor] = useState(index);
  const [isPending, setIsPending] = useState(false);

  // New architecture: write to selectionStore — useSelectionURLSync handles URL sync automatically
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { openMediaViewerInMapMode } = usePopupActions();
  const { closeMediaDetail } = useMediaActions();

  const debouncedSlideChange = useDebounceCallback((e: KeyboardEvent) => {
    const key = e.key?.toLowerCase();
    if (key === "arrowleft") {
      goToPrev();
      return;
    }
    if (key === "arrowright") {
      goToNext();
    }
  });

  const config = useConfig();

  useEffect(() => {
    setCursor(index);
  }, [index]);

  const mediaApiEndpoint = useMemo(() => {
    if (config?.apiEndpoints) {
      return config.apiEndpoints.find((endpoint) => endpoint.tableName === "media");
    }
    return null;
  }, [config]);

  async function goToPrev() {
    const prevCursor = cursor > 0 ? cursor - 1 : 0;
    setCursor(prevCursor);
    onChange?.(prevCursor, items?.[prevCursor]);
  }

  function goToNext() {
    if (!items) return;
    const nextCursor = cursor >= items?.length - 1 ? cursor : cursor + 1;
    setCursor(nextCursor);
    onChange?.(nextCursor, items?.[nextCursor]);
  }

  useEffect(() => {
    document.addEventListener("keydown", debouncedSlideChange);

    return () => {
      document.removeEventListener("keydown", debouncedSlideChange);
    };
  }, []);
  /**
   * "Find Related" for a media item in the carousel.
   *
   * Instead of calling fetchAndSelectEntityRelationships from the old SelectionContext
   * (which fetched data, updated context, and manually set URL params), we now:
   * 1. Write the media item's ID + endpoint to the selectionStore
   * 2. useSelectionURLSync automatically syncs URL params
   * 3. useSelectedEntity (React Query) automatically fetches L2 + L3 data
   */
  async function handleFetchRelationship() {
    if (!currentItem || !mediaApiEndpoint) return;

    try {
      setIsPending(true);

      // Update the selection store — this is the new source of truth
      setSelectedPrimaryStates({ selectedEndpoint: mediaApiEndpoint, selectedEntityId: currentItem.id, showRelationShip: true });

      // Open media-viewer already in map mode (single atomic state update — no flicker)
      openMediaViewerInMapMode();
    } catch (err) {
      const error = err as Error;
      enqueueSnackbar(error?.message || "Failed to fetch relationship.", { variant: "error" });
    } finally {
      setIsPending(false);
    }
  }

  // cleanup media details modal state
  useEffect(() => {
    return () => {
      closeMediaDetail();
    };
  }, []);

  const isLoading = isPending;
  const shouldDisablePrevBtn = isLoading || cursor === 0 || items?.length === 0;
  const shouldDisableNextBtn = isLoading || cursor >= items?.length - 1 || items?.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        position: "relative",
        alignItems: "center",
        justifyContent: "center",
        gap: "2.5rem",
        pointerEvents: "auto",
        height: "100%",
        minHeight: "100%"
      }}
    >
      <Box sx={{ position: "absolute", zIndex: 2, left: 24 }}>
        <Tooltip title='Show previous media'>
          <SliderButton onClick={goToPrev} disabled={shouldDisablePrevBtn}>
            <ChevronLeft sx={{ fontSize: "2rem" }} />
          </SliderButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          minHeight: 0,
          width: "100%",
          height: "100%",
          position: "relative",
          flex: "1",
          "& > *": {
            paddingInline: "0"
          }
        }}
      >
        <EntityMediaOverview
          entity={currentItem}
          mode='expanded'
          onFindRelatedItem={handleFetchRelationship}
          isLoading={isPending}
          mediaPreviewOptions={{ onClose: closeMediaDetail }}
        />
      </Box>
      <Box sx={{ position: "absolute", zIndex: 2, right: 24 }}>
        <Tooltip title='Show next media'>
          <SliderButton onClick={goToNext} disabled={shouldDisableNextBtn}>
            <ChevronRight sx={{ fontSize: "2rem" }} />
          </SliderButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
